import { defineStore } from 'pinia';
import { computed, Ref, ref, watch } from 'vue';
import IodineGrouperService, { GrouperLayoutMode } from 'src/services/IodineGrouperService';
import { LayoutState } from 'src/contracts/encoderState';
import { getEncoderState } from 'stores/state-helper';
import {Subscription} from 'rxjs';

/**
 * Enum for the panes supported in grouper.
 */
export enum GrouperPane {
  CODES = 'codes',
  SEARCH = 'search',
  REFERENCES = 'references',
  RESOURCES = 'resources'
}

export const useLayoutStore = defineStore('layoutstore', () => {

  const layoutMode = ref(GrouperLayoutMode.consolidated);
  const getLayoutMode = computed(() => layoutMode.value);

  const selectedTab = ref(GrouperPane.CODES);
  const researchTab = ref('research');
  watch(selectedTab, (newTab) => {
    setSelectedTab(newTab);
  });

  const codesSelected = ref(true);
  const isCodesSelected = computed(() => codesSelected.value);
  const searchSelected = ref(false);
  const isSearchSelected = computed(() => searchSelected.value);
  const referencesSelected = ref(false);
  const isReferencesSelected = computed(() => referencesSelected.value);
  const resourcesSelected = ref(false);
  const isResourcesSelected = computed(() => resourcesSelected.value);

  watch(resourcesSelected, (newValue) => {
    IodineGrouperService.resourcesStateChange(newValue);
  });

  const isCodesNotSelectedAndSearchSelected = computed(() => !codesSelected.value && searchSelected.value);
  const isCodesNotSelectedAndSearchNotSelectedAndReferenceSelected = computed(() => !codesSelected.value && !searchSelected.value && referencesSelected.value);
  const isCodesSelectedAndSearchSelected = computed(() => codesSelected.value && searchSelected.value);

  const getPaneOne : Ref<GrouperPane> = computed(() => {
    if (codesSelected.value) {
      return GrouperPane.CODES;
    } else if (searchSelected.value) {
      return GrouperPane.SEARCH;
    } else if (referencesSelected.value) {
      return GrouperPane.REFERENCES;
    } else {
      return GrouperPane.RESOURCES;
    }
  });

  const getPaneTwo = computed(() => {
    if (codesSelected.value && searchSelected.value) {
      return GrouperPane.SEARCH;
    } else if ((!codesSelected.value && searchSelected.value && referencesSelected.value) || (codesSelected.value && !searchSelected.value && referencesSelected.value)) {
      return GrouperPane.REFERENCES;
    } else {
      return GrouperPane.RESOURCES;
    }
  });

  const getPaneThree = computed(() => {
    if (resourcesSelected.value) {
      return GrouperPane.RESOURCES;
    } else {
      return GrouperPane.REFERENCES;
    }
  });

  function setCodesSelected(value: boolean) {
    codesSelected.value = value;
    if (value) {
      selectedTab.value = GrouperPane.CODES;
    }
  }

  function setSearchSelected(value: boolean) {
    searchSelected.value = value;
    if (value) {
      selectedTab.value = GrouperPane.SEARCH;
    }
  }

  function setReferencesSelected(value: boolean) {
    referencesSelected.value = value;
    if (value) {
      selectedTab.value = GrouperPane.REFERENCES;
    }
  }

  function setResourcesSelected(value: boolean) {
    resourcesSelected.value = value;
  }

  function changeMode(mode: GrouperLayoutMode, notify = true) {
    layoutMode.value = mode;
    if (notify) {
      IodineGrouperService.changeMode(mode, getEncoderState());
    }
  }

  function requestClose() {
    IodineGrouperService.close(getEncoderState());
  }

  function getLayoutState(): LayoutState {
    return {
      layoutMode: layoutMode.value,
      isCodesSelected: isCodesSelected.value,
      isReferencesSelected: isReferencesSelected.value,
      isResourcesSelected: isResourcesSelected.value,
      isSearchSelected: isSearchSelected.value
    };
  }

  function setLayoutState(layoutState: LayoutState): void {

    layoutMode.value = layoutState?.layoutMode;
    codesSelected.value = layoutState?.isCodesSelected;
    searchSelected.value = layoutState?.isSearchSelected;
    referencesSelected.value = layoutState?.isReferencesSelected;
    resourcesSelected.value = layoutState?.isResourcesSelected;

    if (codesSelected.value) {
      selectedTab.value = GrouperPane.CODES;
    } else if (searchSelected.value) {
      selectedTab.value = GrouperPane.SEARCH;
    } else if (referencesSelected.value) {
      selectedTab.value = GrouperPane.REFERENCES;
    }
  }

  function setSelectedTab(tab: string) {

    switch(tab) {
      case 'codes':
        setCodesSelected(true);
        setReferencesSelected(false);
        setSearchSelected(false);
        break;
      case 'search':
        setCodesSelected(false);
        setReferencesSelected(false);
        setSearchSelected(true);
        break;
      case 'references':
        setCodesSelected(false);
        setReferencesSelected(true);
        setSearchSelected(false);
        break;
    }
  }

  let setModeSubscription: Subscription | undefined;
  function createSetModeSubscription() {
    setModeSubscription = IodineGrouperService.setMode$.subscribe(
      (mode: GrouperLayoutMode) => {

        layoutMode.value = mode;
      }
    );
  }

  function $onMount() {
    createSetModeSubscription();
  }

  function $reset() {
    setModeSubscription && setModeSubscription.unsubscribe();
  }

  return {
    layoutMode,
    getLayoutMode,
    changeMode,
    isCodesSelected,
    isSearchSelected,
    isReferencesSelected,
    isResourcesSelected,
    isCodesNotSelectedAndSearchSelected,
    isCodesNotSelectedAndSearchNotSelectedAndReferenceSelected,
    isCodesSelectedAndSearchSelected,
    setCodesSelected,
    setSearchSelected,
    setReferencesSelected,
    setResourcesSelected,
    getPaneOne,
    getPaneTwo,
    getPaneThree,
    requestClose,
    getLayoutState,
    setLayoutState,
    codesSelected,
    searchSelected,
    referencesSelected,
    resourcesSelected,
    selectedTab,
    researchTab,
    $reset,
    $onMount
  }
});
